/**
 * PartnerFunctionTab — extracted from CustomerForm.js for readability.
 * Receives everything via a single `ctx` bag so the parent keeps
 * full ownership of state management.
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Plus, Trash2 } from 'lucide-react';

const PartnerFunctionTab = ({ ctx }) => {
  const {
    formData,
    availablePartnerFunctions,
    partnerFunctionAssignments,
    setPartnerFunctionAssignments,
    allCustomers,
    customerId,
    handleDeletePartnerFunction,
    mode,
    readOnly,
    loading,
    setActiveTab,
  } = ctx;

  return (
    <>
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Partner Function Assignments</h3>
                  </div>

                  {availablePartnerFunctions.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800">
                        <strong>Note:</strong> No partner functions are configured for the selected Account Group. 
                        Partner functions must be assigned to the Account Group before they can be added here.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-gray-200">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner Function</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner Number</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner Name</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {availablePartnerFunctions.map((pf) => {
                            const assignments = partnerFunctionAssignments.filter(
                              a => a.partner_function_id === pf.partner_function_id
                            );
                            
                            // If no assignments, show one empty row
                            if (assignments.length === 0) {
                              return (
                                <tr key={`${pf.partner_function_id}-0`} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium">{pf.partner_function_id}</td>
                                  <td className="px-4 py-3 text-gray-700">{pf.name}</td>
                                  <td className="px-4 py-3">
                                    <Select
                                      value=""
                                      onValueChange={(value) => {
                                        const newAssignment = {
                                          partner_function_id: pf.partner_function_id,
                                          partner_number: value
                                        };
                                        setPartnerFunctionAssignments([...partnerFunctionAssignments, newAssignment]);
                                      }}
                                    >
                                      <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select Customer/Vendor" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {allCustomers.map((customer) => (
                                          <SelectItem key={customer.customer_id} value={customer.customer_id}>
                                            {customer.customer_id} - {customer.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="px-4 py-3 text-gray-400">–</td>
                                  <td className="px-4 py-3 text-center">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        // Just show message to select partner first
                                        toast.info('Select a partner number first');
                                      }}
                                      className="text-orange-600 hover:text-orange-900"
                                      disabled
                                    >
                                      <Plus className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            }
                            
                            // Show all assignments for this partner function
                            return assignments.map((assignment, idx) => {
                              const globalIndex = partnerFunctionAssignments.findIndex(
                                a => a.partner_function_id === assignment.partner_function_id && 
                                     a.partner_number === assignment.partner_number
                              );
                              const partnerCustomer = allCustomers.find(c => c.customer_id === assignment.partner_number);
                              const isLastRow = idx === assignments.length - 1;
                              
                              return (
                                <tr key={`${pf.partner_function_id}-${idx}`} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium">{pf.partner_function_id}</td>
                                  <td className="px-4 py-3 text-gray-700">{pf.name}</td>
                                  <td className="px-4 py-3">
                                    <Select
                                      value={assignment.partner_number}
                                      onValueChange={(value) => {
                                        const updated = [...partnerFunctionAssignments];
                                        updated[globalIndex] = {
                                          ...updated[globalIndex],
                                          partner_number: value
                                        };
                                        setPartnerFunctionAssignments(updated);
                                      }}
                                    >
                                      <SelectTrigger className="w-full">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {allCustomers.map((customer) => (
                                          <SelectItem key={customer.customer_id} value={customer.customer_id}>
                                            {customer.customer_id} - {customer.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="px-4 py-3 text-gray-700">
                                    {partnerCustomer ? partnerCustomer.name : '–'}
                                  </td>
                                  <td className="px-4 py-3 text-center space-x-2">
                                    {isLastRow && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          // Check for duplicate
                                          const newAssignment = {
                                            partner_function_id: pf.partner_function_id,
                                            partner_number: ''
                                          };
                                          setPartnerFunctionAssignments([...partnerFunctionAssignments, newAssignment]);
                                          toast.success('New row added. Please select a partner number.');
                                        }}
                                        className="text-orange-600 hover:text-orange-900"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeletePartnerFunction(globalIndex)}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex justify-between pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setActiveTab('sales')}>
                      ← Back to Sales & Distribution
                    </Button>
                    {!readOnly && (
                      <Button
                        type="submit"
                        disabled={loading || !formData.account_group_id}
                        className={mode === 'create' ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700"}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {mode === 'create' ? 'Creating...' : 'Updating...'}
                          </>
                        ) : (
                          mode === 'create' ? 'Create Customer' : 'Update Customer'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
    </>
  );
};

export default PartnerFunctionTab;
